/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx, css } from '@compiled/react';
import { Box, Stack } from '@atlaskit/primitives';
import { Card } from '@atlaskit/media-card';
import { token } from '@atlaskit/tokens';
import type { FileIdentifier } from '@atlaskit/media-client';
import type { MediaClientConfig } from '@atlaskit/media-core';
import Tooltip from '@atlaskit/tooltip';
import { Truncate } from '@atlaskit/media-ui/truncateText';
import { formatDate } from '@atlaskit/media-ui/formatDate';
import { fg } from '@atlassian/jira-feature-gating';
import { mediaFeatureFlags } from '../../../../../../media-feature-flags.tsx';
import type { AttachmentActionItem } from '../../types.tsx';
import { GRID_ITEM_DIMENSIONS } from '../../constants.tsx';
import { DateFormatter } from './DateFormatter.tsx';
import { TitleBoxWrapper, TitleBoxHeader, TitleBoxFooter } from './TitleBox.tsx';
import MediaCardActions from './MediaCardActions.tsx';

const { height, infoSectionHeight } = GRID_ITEM_DIMENSIONS;

const cardDimensions = {
	width: '100%',
	height: height - infoSectionHeight,
};

const placeholderText = ' ';

const MediaCardItem = ({
	item,
	mediaClientConfig,
	shouldEnableDownloadButton,
	attachmentIdentifiers,
	totalCount,
}: {
	item: AttachmentActionItem;
	mediaClientConfig: MediaClientConfig;
	shouldEnableDownloadButton: boolean;
	attachmentIdentifiers: FileIdentifier[];
	totalCount: number;
}) => {
	const tooltipContent = `${item.filename}\n${item.createdDate ? formatDate(item.createdDate) : placeholderText}`;

	return (
		<div
			css={mediaCardStyle}
			key={item.identifier.id}
			data-testid="issue-view-base.content.attachment.grid-view.ui.components.media-card.media-card-item"
			aria-label={item.filename}
		>
			<Tooltip tag="span" content={tooltipContent}>
				<Stack>
					<div css={[mediaCardContainerStyle]}>
						<Card
							mediaClientConfig={mediaClientConfig}
							mediaViewerItems={attachmentIdentifiers}
							useInlinePlayer={false}
							shouldOpenMediaViewer
							dimensions={cardDimensions}
							featureFlags={mediaFeatureFlags()}
							disableOverlay
							shouldEnableDownloadButton={
								!fg('block_attachment_download_on_data_export_dsp_rule') ||
								shouldEnableDownloadButton
							}
							{...item}
							testId={`issue.views.issue-base.content.attachment.grid-view.attachment-id.${item.identifier.attachmentId}`}
						/>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<div css={blanketStyle} className="media-card-blanket" />
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<div css={[actionsContainerStyle]} className="media-card-actions">
							<MediaCardActions item={item} totalCount={totalCount} />
						</div>
					</div>
					<TitleBoxWrapper>
						<TitleBoxHeader>
							<Truncate text={item.filename || ''} startFixedChars={3} endFixedChars={4} />
						</TitleBoxHeader>
						<TitleBoxFooter>
							<Box>
								{item.createdDate ? (
									<DateFormatter timestamp={item.createdDate} />
								) : (
									placeholderText
								)}
							</Box>
						</TitleBoxFooter>
					</TitleBoxWrapper>
				</Stack>
			</Tooltip>
		</div>
	);
};

export default MediaCardItem;

const mediaCardStyle = css({
	// https://hello.atlassian.net/wiki/spaces/DST/pages/3225764567/Guide+for+Migrating+to+Spacing+Tokens#The-value-I%E2%80%99m-migrating-is-too-large-to-be-represented-with-a-token
	boxSizing: 'border-box',
	minWidth: '138px',
	width: '100%',
	height: '130px',
	cursor: 'pointer',
	borderRadius: token('border.radius.100'),
	overflow: 'hidden',
	border: `1px solid ${token('color.border')}`,
	transition: 'background-color 0.2s',
	backgroundColor: 'elevation.surface.raised',
	outlineOffset: token('space.025'),
	outlineWidth: token('border.width.outline'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover .media-card-actions, &:focus-within .media-card-actions': {
		opacity: 1,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover .media-card-blanket, &:focus-within .media-card-blanket': {
		display: 'block',
	},

	'&:focus-within': {
		outlineStyle: 'solid',
		outlineColor: token('color.border.focused'),
	},
});

const actionsContainerStyle = css({
	position: 'absolute',
	top: '0',
	right: '0',
	zIndex: '1',
	opacity: 0,
	transition: 'opacity 0.2s ease-in-out',
});

const mediaCardContainerStyle = css({
	position: 'relative',
});

const blanketStyle = css({
	position: 'absolute',
	width: '100%',
	height: '100%',
	maxHeight: '100%',
	maxWidth: '100%',
	left: 0,
	top: 0,
	transition: 'all .3s',
	backgroundColor: token('color.blanket'),
	zIndex: '1',
	pointerEvents: 'none',
	display: 'none',
});
